import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { EnvelopeSimple, CheckCircle, Sparkle } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email capture card shown on each free AI tool result.
 * - Saves email + result snapshot as a lead (DB)
 * - Optionally emails the result back to the user (Resend; console fallback)
 *
 * Props:
 *  - toolSlug: 'geo-checkup' | 'rakip-radar' | 'rapor-analitik'
 *  - resultSnapshot: object (the AI result to back-reference)
 */
export default function EmailCaptureCard({ toolSlug, resultSnapshot }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [delivered, setDelivered] = useState(true);
  const [coupon, setCoupon] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('tools.capture.invalid'));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/tools/leads`, {
        email: trimmed,
        tool_slug: toolSlug,
        result_snapshot: resultSnapshot || {},
        consent_marketing: marketing,
      });
      setDelivered(Boolean(data?.delivered));
      setCoupon(data?.coupon || null);
      setDone(true);
      toast.success(data?.delivered ? t('tools.capture.success.delivered') : t('tools.capture.success.queued'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('tools.capture.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 flex items-start gap-4" data-testid="email-capture-success">
        <CheckCircle size={28} weight="fill" className="text-emerald-400 shrink-0" />
        <div className="flex-1">
          <h4 className="font-semibold text-white mb-1">{t('tools.capture.success.title')}</h4>
          {delivered ? (
            <p className="text-sm text-white/70">
              {t('tools.capture.success.delivered_body', { email })}
              {marketing ? ' ' + t('tools.capture.success.delivered_marketing') : ''}
            </p>
          ) : (
            <p className="text-sm text-white/70">
              {t('tools.capture.success.queued_body', { email })}
            </p>
          )}
          {coupon && (
            <div className="mt-4 p-4 rounded-xl bg-[#3CD9B8] text-[#0F1B36]" data-testid="email-capture-coupon">
              <div className="text-xs uppercase font-semibold tracking-wider mb-1">
                {t('tools.capture.coupon.title')}
              </div>
              <div className="text-2xl font-mono font-bold tracking-wider">{coupon}</div>
              <div className="text-xs mt-1 opacity-80">{t('tools.capture.coupon.hint')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="email-capture-card"
      className="mt-8 rounded-2xl border border-[#3CD9B8]/30 bg-gradient-to-br from-[#3CD9B8]/10 to-transparent p-6 sm:p-8"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-[#3CD9B8]/10 text-[#3CD9B8]"><EnvelopeSimple size={22} weight="bold" /></div>
        <div>
          <h4 className="font-semibold text-white mb-1">{t('tools.capture.title')}</h4>
          <p className="text-sm text-white/70">{t('tools.capture.desc')}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          autoComplete="email"
          placeholder={t('tools.capture.placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="email-capture-input"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-[#3CD9B8] transition"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="email-capture-submit"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 whitespace-nowrap"
        >
          {submitting ? <><Sparkle size={16} className="animate-pulse" /> {t('tools.capture.sending')}</> : t('tools.capture.submit')}
        </button>
      </div>
      <label className="mt-3 flex items-start gap-2 text-xs text-white/60 cursor-pointer">
        <input
          type="checkbox"
          checked={marketing}
          onChange={(e) => setMarketing(e.target.checked)}
          data-testid="email-capture-marketing"
          className="mt-0.5 accent-[#3CD9B8]"
        />
        <span>{t('tools.capture.marketing')}</span>
      </label>
    </form>
  );
}
